import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Mail, BookOpen, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { useAuth } from '../contexts/AuthContext';


export const ExportSuccessScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { platform, productId, fileName } = location.state || {};
  
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleDownload = async () => {
    try {
      toast.loading(`Preparing ${platform} file...`);
      
      const response = await api.get(
        `/export/${platform}/${productId}`,
        { 
          responseType: 'blob',
          timeout: 30000,
          
        }
      );
      
      toast.dismiss();
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `${platform}_product_${productId}.csv`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('✓ File downloaded! Check your Downloads folder.', {
        duration: 5000
      });
    } catch (error) {
      toast.dismiss();
      console.error('Download error:', error);
      
      if (error.code === 'ECONNABORTED') {
        toast.error('Download timed out. Please check your connection and try again.');
      } else {
        toast.error('Failed to download. Please try again or use email option.');
      }
    }
  };

  const handleSendEmail = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    
    try {
      await api.post(`/export/email`, {
        product_id: productId,
        platform,
        email
      });
      
      toast.success(`✓ File sent to ${email}! Check your inbox.`, {
        duration: 6000
      });
      setEmail('');
    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Failed to send email. Please try download option instead.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleViewGuide = () => {
    navigate('/platform-guide', {
      state: { platform, productId }
    });
  };

  if (!platform || !productId) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Invalid export request</p>
          <button
            onClick={() => navigate('/my-products')}
            className="px-6 py-3 bg-[#128C7E] text-white rounded-xl font-bold"
          >
            Go to My Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="Export Success" showBack={true} />

        <div className="p-6 space-y-6">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-3">
              Your product file is ready! 🎉
            </h1>
            <p className="text-base text-[#667781] leading-relaxed">
              Choose how you want to get your {platform.charAt(0).toUpperCase() + platform.slice(1)} listing file
            </p>
          </motion.div>

          {/* Action Buttons */}
          <div className="space-y-4">
            {/* Download Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              onClick={handleDownload}
              className="w-full h-16 bg-[#128C7E] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg"
              data-testid="download-file-button"
            >
              <Download size={28} />
              Download File
            </motion.button>

            {/* Email Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-50 rounded-2xl p-5 border-2 border-gray-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <Mail size={24} className="text-[#128C7E]" />
                <h3 className="text-lg font-bold text-slate-800">
                  Send to Email
                </h3>
              </div>
              <p className="text-sm text-[#667781] mb-4">
                Get the file in your inbox (optional)
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full h-14 px-4 bg-white border-2 border-gray-200 rounded-xl text-base focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none mb-3"
                data-testid="email-input"
              />
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !email}
                className="w-full h-14 bg-white border-2 border-[#128C7E] text-[#128C7E] rounded-xl text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-email-button"
              >
                {sendingEmail ? 'Sending...' : 'Send File to Email'}
              </button>
            </motion.div>

            {/* Upload Guide Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              onClick={handleViewGuide}
              className="w-full h-16 bg-[#25D366] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg shadow-green-200"
              data-testid="view-guide-button"
            >
              <BookOpen size={28} />
              How to Upload on {platform.charAt(0).toUpperCase() + platform.slice(1)}
            </motion.button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-start gap-3">
              <AlertCircle size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-base text-blue-900 leading-relaxed">
                  <strong>Next Steps:</strong><br/>
                  1. Download or email the file<br/>
                  2. Read the upload guide<br/>
                  3. Upload to {platform.charAt(0).toUpperCase() + platform.slice(1)}<br/>
                  Done! Your product will be live
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
